import { NgClass, NgFor, NgIf } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from "@angular/material/dialog";
import { BaseModalComponent } from "../base-modal/base-modal.component";
import { CardModel } from "src/swagger/api.client.generated";
import { ErrorDisplayComponent } from "../error-display/error-display.component";
import { SafetyCardCategory } from "../card/card.models";
import { getSafetyCategoryLabel } from "../card/card-type.utils";

export interface EditCardDialogData {
  cardName: string;
  periodMinutes?: number;
  /** Heading shown in the dialog — "Create Your Card" for a new custom card, else "Edit Your Card". */
  title?: string;
  /** Which typed card this dialog is for — drives the disabled "Card type" pill. */
  cardKind: "safety" | "activity";
  /** Only meaningful when cardKind === "safety"; null/omitted for legacy uncategorized cards. */
  safetyCategory?: SafetyCardCategory | null;
}

export type EditCardDialogMode = "edit" | "duplicate";

/** Minimal projection of a deck card used for live duplicate-name validation. */
export interface EditCardDeckEntry {
  cardId: number;
  cardGuid?: string;
  cardName: string;
  periodMinutes: number;
  /** Activity vs Safety. Omitted/null when unknown — an unknown kind is treated as a possible clash. */
  cardKind?: "safety" | "activity" | null;
  /** Safety cards only — the category the card lives in. Null for legacy uncategorized cards. */
  safetyCategory?: SafetyCardCategory | null;
}

export interface EditCardDialogData {
  card: CardModel;
  /** Which action opened the dialog — determines name pre-fill and how the result is saved. */
  mode: EditCardDialogMode;
  /** The patient's active deck, for the client-side duplicate-name check. */
  deckCards: EditCardDeckEntry[];
  /** Mandatory cards lock the name and allow period-override only. */
  isMandatory: boolean;
}

export interface EditCardDialogResult {
  cardName: string;
  periodMinutes?: number;
  mode: EditCardDialogMode;
  description?: string;
}

export const DUPLICATE_NAME_MESSAGE =
  "Card name already exists in your deck. Choose a different name or assign a duration.";

// The API's CardModel.CardGuid is a non-nullable Guid that GetAllCards never sets, so every library
// card arrives with this value. It is not an identity — treat it as "no guid".
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const hasGuid = (guid: string | null | undefined): guid is string =>
  !!guid && guid !== EMPTY_GUID;

@Component({
  selector: "app-dialog-edit-card",
  templateUrl: "./dialog-edit-card.component.html",
  styleUrls: ["./dialog-edit-card.component.scss"],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIf,
    NgFor,
    NgClass,
    BaseModalComponent,
    ErrorDisplayComponent,
  ],
})
export class DialogEditCardComponent implements OnInit {
  editCardForm: FormGroup;
  numberOptions = Array.from({ length: 60 }, (_, i) => i + 1);
  readonly duplicateNameMessage = DUPLICATE_NAME_MESSAGE;

  /** Text shown in the disabled "Card type" pill, e.g. "Star Card (Feelings)" or "Activity Card". */
  get cardTypeLabel(): string {
    if (this.data.cardKind === "activity") return "Activity Card";
    const label = getSafetyCategoryLabel(this.data.safetyCategory);
    return label ? `Star Card (${label})` : "Star Card";
  }

  /** CSS class driving the pill's gradient/color fill — see dialog-edit-card.component.scss. */
  get cardTypeCssClass(): string {
    if (this.data.cardKind === "activity") return "card-type-activity";
    return this.data.safetyCategory ? `card-type-${this.data.safetyCategory}` : "card-type-default";
  }

  constructor(
    public dialogRef: MatDialogRef<DialogEditCardComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditCardDialogData,
    private fb: FormBuilder
  ) {
    // Duplicate pre-fills the name with a " 2" suffix to nudge a rename — except for mandatory cards
    // where the name is locked and only the period can change.
    // "blank" is the placeholder sentinel for an un-named custom card — treat it as no prefill.
    const resolvedName = (data.card.cardName === "blank") ? "" : (data.card.cardName ?? "");
    const initialName =
      data.mode === "duplicate"
        ? `${resolvedName} 2`.trim()
        : resolvedName;

    this.editCardForm = this.fb.group({
      cardName: [
        { value: initialName, disabled: data.isMandatory && data.mode === "edit" },
        [Validators.required, this.duplicateNameValidator()],
      ],
      periodMinutes: [
        data.card.periodMinutes || null,
        [Validators.min(1), Validators.max(60)],
      ],
      description: [data.card.cardDescription ?? ""],
    });
  }

  ngOnInit() {
    // Re-run the name check when the duration changes, so "shopping" + a new duration live-clears
    // the duplicate-name error (same name is allowed with a different duration).
    this.editCardForm.get("periodMinutes")!.valueChanges.subscribe(() => {
      this.editCardForm.get("cardName")!.updateValueAndValidity({ emitEvent: false });
    });

    // Show duplicate-name errors on open when a name is pre-filled (mat-error only renders for
    // touched controls). Skip when the name is empty — touching an empty required field would show
    // an error border before the user has typed anything.
    const initialName = this.editCardForm.get('cardName')!.value;
    if (initialName) {
      this.editCardForm.markAllAsTouched();
    }
  }

  // Flags `duplicateName` on the name control when the patient's deck already holds a card with the
  // same (trimmed, case-insensitive) name AND the same duration (FLIP-312) — the same name with a
  // different duration is fine. Which cards count as a clash depends on type:
  //  - Safety card: only Safety cards in the SAME category.
  //  - Activity card: any Activity card.
  //  - An Activity card and a Safety card never clash. A deck card of unknown kind is treated
  //    conservatively (as if it were the same kind and category).
  // An in-place edit excludes the source card (it can't clash with itself); a duplicate is a
  // brand-new card, so it CAN clash with the source card and that exclusion must not apply.
  // Self-identification prefers cardGuid (unique per instance) over cardId, which can be shared
  // across unsaved template cards (e.g. placeholder 9011/9012) — but only when BOTH sides carry a
  // real guid: library cards all arrive with the empty guid (see EMPTY_GUID).
  private duplicateNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const name = (control.value ?? "").trim().toLowerCase();
      if (!name) return null;

      const period = Number(this.editCardForm?.get("periodMinutes")?.value) || 0;
      const isDuplicate = this.data.mode === "duplicate";
      const sourceGuid = this.data.card.cardGuid;
      const sourceId = this.data.card.cardId;
      const isSafety = this.data.cardKind === "safety";
      const category = this.data.safetyCategory ?? null;

      const clash = (this.data.deckCards ?? []).some((c) => {
        const isSelf = hasGuid(sourceGuid) && hasGuid(c.cardGuid)
          ? c.cardGuid === sourceGuid
          : c.cardId === sourceId;
        if (!isDuplicate && isSelf) return false;
        if ((c.cardName ?? "").trim().toLowerCase() !== name) return false;

        if (c.cardKind && c.cardKind !== this.data.cardKind) return false; // Activity vs Safety
        if (isSafety && c.cardKind === "safety" && (c.safetyCategory ?? null) !== category) {
          return false; // Safety cards in different categories may share a name
        }
        return (Number(c.periodMinutes) || 0) === period;
      });
      return clash ? { duplicateName: true } : null;
    };
  }

  save(): void {
    if (this.editCardForm.invalid) {
      return;
    }

    // getRawValue so the locked (disabled) mandatory name is still emitted.
    const raw = this.editCardForm.getRawValue();
    const result: EditCardDialogResult = {
      cardName: raw.cardName,
      periodMinutes: raw.periodMinutes ?? undefined,
      mode: this.data.mode,
      description: raw.description || undefined,
    };
    this.dialogRef.close(result);
  }

  close(): void {
    this.dialogRef.close();
  }
}
